/**
 * Gunner на крыше штаба (ISSUE-52) — только отрисовка. В симуляции он стоит на платформе: дальность, цели, снаряды и журнал
 * волны считаются оттуда, баланс не меняется. Снаряд на экране вылетает с крыши и за полёт сходится к настоящей
 * траектории, в точку попадания приходит точно.
 */
import type { HeroId, Vec2 } from "@newfungame/contracts";
import type { Point } from "./iso";

export const ROOF_HERO: HeroId = "gunner";
/** Высота ног героя над подошвой штаба (стоит на турели: крыша 1,175 + турель 0,9 клетки), те же единицы, что `MOUNT_HEIGHT`. */
export const ROOF_HEIGHT = 1.27;

/** Доля экранного сдвига крыши, оставшаяся снаряду: 1 — в начале полёта, 0 — у цели. */
export function roofShare(p: { pos: Vec2; from: Vec2; to: Vec2 }): number {
  const total = Math.hypot(p.to.x - p.from.x, p.to.y - p.from.y);
  if (total === 0) return 0;
  return Math.min(1, Math.max(0, Math.hypot(p.to.x - p.pos.x, p.to.y - p.pos.y) / total));
}

/** Экранный сдвиг от платформы героя к крыше. */
export function roofOffset(roof: Point, platform: Point): Point {
  return { x: roof.x - platform.x, y: roof.y - platform.y };
}

/**
 * Платформа прямо за штабом (ISSUE-123): её закрывают штаб и Gunner на крыше, герой на ней не виден. На экране она меняется
 * местами с опустевшей платформой Gunner — только отрисовка, как у крыши: симуляция и журнал волны прежние, снаряды и лучи
 * героя вылетают с новой платформы и сходятся к настоящей траектории ({@link roofShare}).
 */
export interface BackSwap {
  /** Платформа за штабом (мир). */
  back: Vec2;
  /** Опустевшая платформа героя с крыши (мир). */
  roof: Vec2;
}

const samePos = (a: Vec2, b: Vec2): boolean => a.x === b.x && a.y === b.y;

/** Обмен нужен, если герой с крыши есть, а платформа за штабом — не его. */
export function backSwap(hq: Vec2, platforms: readonly { pos: Vec2 }[], roofHero: Vec2 | null): BackSwap | null {
  if (!roofHero) return null;
  const back = platforms.find((p) => p.pos.x === hq.x && p.pos.y > hq.y)?.pos;
  if (!back || samePos(back, roofHero)) return null;
  return { back, roof: roofHero };
}

/** Где на экране стоит мировая точка платформы: две платформы обмена меняются местами, остальные на месте. */
export function displayPos(pos: Vec2, swap: BackSwap | null): Vec2 {
  if (!swap) return pos;
  if (samePos(pos, swap.back)) return swap.roof;
  if (samePos(pos, swap.roof)) return swap.back;
  return pos;
}

/** Стоит ли мировая точка на платформе за штабом (герой, выстрел, луч). */
export function onBack(pos: Vec2, swap: BackSwap | null): boolean {
  return swap !== null && samePos(pos, swap.back);
}
